import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import { GeckoInspectionType, GeckoMeasurement } from 'types'

/**
 * Metric definition for inspection cards
 * @template T The type of value returned by getValue
 */
export type MetricDefinition<T = unknown> = {
  key: string
  label: string
  getValue: (inspection: EnrichedGeckoInspection) => T
  format?: (value: T, inspection: EnrichedGeckoInspection) => string
  component: 'value' | 'progress'
  shouldShow?: (inspection: EnrichedGeckoInspection) => boolean
}

/**
 * Formats a GeckoMeasurement for display
 */
const formatMeasurement = (measurement: GeckoMeasurement): string => {
  return measurement.unit.formatNumber({
    num: measurement.num,
    locale: 'en-US',
    variant: 'narrow',
  })
}

/**
 * Scan Area metric - shows the scanned area with unit
 */
export const scanAreaMetric: MetricDefinition<GeckoMeasurement> = {
  key: 'scanArea',
  label: 'Scanned Area',
  getValue: (inspection) => inspection.data.scanArea,
  format: (value) => formatMeasurement(value),
  component: 'value',
  shouldShow: (inspection) =>
    inspection.data.inspection_type === 'raut' &&
    inspection.data.scanArea.num > 0,
}

/**
 * Maximum Possible Scan Area metric
 */
export const maxScanAreaMetric: MetricDefinition<GeckoMeasurement> = {
  key: 'maxScanArea',
  label: 'Maximum Possible Scan Area',
  getValue: (inspection) => inspection.data.maximumPossibleScanArea,
  format: (value) => formatMeasurement(value),
  component: 'value',
  shouldShow: (inspection) => inspection.data.maximumPossibleScanArea.num > 0,
}

/**
 * Inspection Progress metric - shows completion percentage with progress bar
 */
export const progressMetric: MetricDefinition<string> = {
  key: 'progress',
  label: 'Inspection Progress',
  getValue: (inspection) => inspection.data.percentageProgress.toString(),
  format: (value) => `${parseFloat(value).toFixed(2)}%`,
  component: 'progress',
  shouldShow: (inspection) =>
    inspection.data.inspection_type === 'raut' &&
    inspection.data.derivedStatus !== 'closed',
}

/**
 * Indication Count metric - shows the total number of indications
 */
export const indicationCountMetric: MetricDefinition<number> = {
  key: 'indicationCount',
  label: 'Indication Count',
  getValue: (inspection) => inspection.data.indicationCount,
  format: (value) => value.toString(),
  component: 'value',
}

/**
 * Highest DRA metric - shows the highest DRA rating from all RAUT report indications
 */
export const worstDRAMetric: MetricDefinition<number | undefined> = {
  key: 'worstDRA',
  label: 'Highest DRA',
  getValue: (inspection) => inspection.data.worstDRA,
  format: (value) => (value !== undefined ? value.toString() : 'N/A'),
  component: 'value',
  shouldShow: (inspection) =>
    inspection.data.inspection_type === 'raut' &&
    inspection.data.worstDRA !== undefined,
}

/**
 * All available metrics
 */
export const ALL_METRICS = {
  scanArea: scanAreaMetric,
  maxScanArea: maxScanAreaMetric,
  progress: progressMetric,
  indicationCount: indicationCountMetric,
  worstDRA: worstDRAMetric,
} as const

/**
 * Map of inspection type to what metrics should be displayed for cards of that type
 */
export const INSPECTION_TYPE_METRICS: Record<
  GeckoInspectionType,
  (keyof typeof ALL_METRICS)[]
> = {
  raut: ['indicationCount', 'scanArea', 'maxScanArea', 'worstDRA', 'progress'],
  rug: ['indicationCount', 'maxScanArea'],
  manual: ['indicationCount', 'maxScanArea'],
  'phased-array': ['indicationCount'],
  trilat: ['indicationCount'],
  lidar: ['indicationCount'],
  ie: ['indicationCount'],
  sasw: ['indicationCount'],
  upe: ['indicationCount'],
  api: ['indicationCount'],
  mfl: ['indicationCount'],
  emat: ['indicationCount'],
  mi: ['indicationCount'],
  art: ['indicationCount'],
  'pulsed-eddy-current': ['indicationCount'],
  drone: ['indicationCount'],
  frp: ['indicationCount'],
  pt: ['indicationCount'],
  'brick-tile-inspection': ['indicationCount'],
  'external-visual': ['indicationCount'],
  'internal-visual': ['indicationCount'],
  'profile-radiography': ['indicationCount'],
  'infrared-thermography': ['indicationCount'],
  'spark-testing': ['indicationCount'],
  'lift-lever-test': ['indicationCount'],
  photogrammetry: ['indicationCount'],
}

/**
 * Gets the metrics that should be displayed for a given inspection
 */
export const getMetricsForInspection = (
  inspection: EnrichedGeckoInspection
): Array<
  | MetricDefinition<GeckoMeasurement>
  | MetricDefinition<string>
  | MetricDefinition<number>
  | MetricDefinition<number | undefined>
> => {
  const inspectionType = inspection.data.inspection_type as string
  const metricKeys = INSPECTION_TYPE_METRICS[inspectionType] || []

  return metricKeys
    .map((key) => ALL_METRICS[key])
    .filter((metric) => {
      if (!metric) return false
      if (!metric.shouldShow) return true
      return metric.shouldShow(inspection)
    })
}
